import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, Rocket, Check, AlertTriangle, PartyPopper, ChevronDown, Users,
} from 'lucide-react';
import { useState } from 'react';
import type { WizardMode } from '@/stores/campaignWizardStore';
import { useWizardStore } from '@/stores/campaignWizardStore';
import { getCampaignTypeByKey } from '@/lib/campaignTypeRegistry';
import { resolvePinStyle } from '@/lib/pinStyles';
import { creativeCopyIssues } from '@/lib/creativeCopy';
import { splitContractAmount } from '@/lib/campaignPackages';

// ─── Helpers ────────────────────────────────────────────────────────────────

function diffDays(start: string, end: string): number {
  if (!start || !end) return 0;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(1, Math.round(ms / 86400000));
}

function fmtLong(iso: string): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Categorise the campaign's schedule relative to NOW so the publish
 * summary can show an explicit "live immediately" vs. "scheduled" cue.
 *
 * The mobile resolver gates campaigns on `starts_at <= NOW() AND
 * ends_at > NOW()` — a campaign published with a future start date
 * legitimately won't appear in-app until that date. Without surfacing
 * this on the review screen, operators publish a Festival campaign for
 * "next month" and then panic when they don't see it the same day.
 */
function deriveScheduleHint(startIso: string, endIso: string): {
  tone: 'live' | 'starts_today' | 'scheduled' | 'invalid';
  message: string;
  daysUntilStart: number;
} {
  if (!startIso || !endIso) {
    return { tone: 'invalid', message: 'Pick start and end dates first.', daysUntilStart: 0 };
  }
  const now = Date.now();
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return { tone: 'invalid', message: 'Invalid date.', daysUntilStart: 0 };
  }
  if (end <= start) {
    return { tone: 'invalid', message: 'End date must be after start date.', daysUntilStart: 0 };
  }
  if (now >= start && now <= end) {
    return {
      tone: 'live',
      message: `Goes live the moment you publish and runs through ${fmtLong(endIso)}.`,
      daysUntilStart: 0,
    };
  }
  if (now > end) {
    return {
      tone: 'invalid',
      message: 'End date is in the past. Pick a future window.',
      daysUntilStart: 0,
    };
  }
  // Future start
  const days = Math.max(1, Math.ceil((start - now) / 86400000));
  if (days === 1) {
    return {
      tone: 'starts_today',
      message:
        'Goes live tomorrow at midnight (AST). The campaign will not appear in the app until then. Use "Preview now" on the Festivals page to QA on your device sooner.',
      daysUntilStart: 1,
    };
  }
  return {
    tone: 'scheduled',
    message: `Goes live in ${days} days (${fmtLong(startIso)}). The campaign will not appear in the app until then. Use "Preview now" on the Festivals page to QA on your device sooner.`,
    daysUntilStart: days,
  };
}

const numOrNull = (s: string): number | null => {
  if (!s || !s.trim()) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

// ─── Props ──────────────────────────────────────────────────────────────────

interface PublishStepProps {
  mode: WizardMode;
  onPublished: () => void;
  onClose: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function PublishStep({ mode, onPublished, onClose }: PublishStepProps) {
  const store = useWizardStore(mode);
  const {
    selectedBusiness, templateKey, campaignType, campaignTypes,
    headline, body, ctaText, backgroundColor,
    creativeMode, artImageUrl, uploadImageUrl, uploadVideoUrl, gallerySelectedLogoUrl,
    startDate, endDate, durationDays,
    bidXcd, cpmRateXcd, flatFeeXcd,
    maxImpressionsPerDayPerUser, coolDownHours, maxDailyImpressions,
    invoiceStatus, invoiceAmountXcd,
    corridorIds, audience,
    hourStart, hourEnd,
    weatherTriggers, targetRadiusKm,
    festivalMode, eventSlug, eventLocationLabel,
    pushAfterPublish, publishing,
    brandBrief,
    // Festival linkage (mode === 'festival' only)
    selectedFestival, festivalTier, festivalSubEventSlug,
    contractedAmountXcd, invoiceNumber,
    teamPreviewOnly,
  } = store;

  const [showJson, setShowJson] = useState(false);

  const scheduleHint = diffDays(startDate, endDate) > 0
    ? deriveScheduleHint(startDate, endDate)
    : null;

  // Templates we will publish — one campaign per type.
  // Falls back to the legacy single `campaignType` if the array is empty
  // (covers any caller that hasn't migrated yet).
  const typesToPublish = campaignTypes.length > 0
    ? campaignTypes
    : (campaignType ? [campaignType] : []);

  const typeDef = campaignType ? getCampaignTypeByKey(campaignType) : undefined;
  // A pin marks the business itself as sponsored for everyone, so it can't
  // be limited to the preview team.
  const hasPinType = typesToPublish.some(
    (tk) => getCampaignTypeByKey(tk)?.placement === 'map_pin_featured',
  );
  const previewOnly = teamPreviewOnly && !hasPinType;

  const logoUrl = gallerySelectedLogoUrl || selectedBusiness?.logo_url || '';
  const heroUrl = artImageUrl || uploadImageUrl || selectedBusiness?.hero_image_url || '';

  // Validation
  const errors: string[] = [];
  if (!selectedBusiness) errors.push('Pick a business on Step 1.');
  if (typesToPublish.length === 0) errors.push('Pick at least one template on Step 2.');
  if (!headline.trim()) errors.push('Write a headline on Step 3.');
  else errors.push(...creativeCopyIssues({ headline, body, ctaText }));
  if (!startDate || !endDate) errors.push('Set start and end dates on Step 5.');
  if (startDate && endDate && diffDays(startDate, endDate) < 1) {
    errors.push('End date must be after the start date.');
  }
  // Map Pin coordinates: use the operator's override if they dragged the
  // pin in the live preview, otherwise fall back to the directory entry.
  // We treat null overrides as "use the registered brand coords"; if BOTH
  // are missing the operator must fix it (either drag the preview pin or
  // edit the brand entry).
  const effectivePinLat = store.pinLatOverride ?? selectedBusiness?.latitude ?? null;
  const effectivePinLng = store.pinLngOverride ?? selectedBusiness?.longitude ?? null;
  if (typesToPublish.includes('sponsored_map_pin') &&
      (effectivePinLat === null || effectivePinLng === null)) {
    errors.push(
      'Map Pin selected but no coordinates set. Drag the pin on the live preview or edit the brand to add a location.',
    );
  }
  if (festivalMode && !eventSlug.trim()) {
    errors.push('Festival Mode is on but no event slug is set on Step 4.');
  }
  // Festival-wizard-mode-specific validation: require both festival
  // and tier from step 0 (the FestivalPicker). If either is missing
  // we cannot link the campaign in festival_sponsorships.
  if (mode === 'festival' && (!selectedFestival || !festivalTier)) {
    errors.push('Pick a festival and a sponsorship tier on Step 1.');
  }

  const canPublish = errors.length === 0 && !publishing;

  /**
   * Publish one campaign per selected template. Each call to the
   * create_campaign_from_wizard RPC creates a separate ad_campaigns row,
   * but they all share the brand, creative and schedule. The contract
   * amount is split between the rows so summed reports match the deal.
   *
   * Behavior on partial failure: we log the error, surface a warning
   * toast for that template, and continue with the remaining templates.
   * The wizard does NOT roll back already-created campaigns.
   */
  const handlePublish = async () => {
    if (!canPublish || !selectedBusiness) return;
    store.set({ publishing: true });

    // Resolve the final creative mode based on what's actually attached.
    // The mobile app only paints a creative image on full-bleed / video
    // surfaces, so any attached still image MUST publish as
    // client_full_bleed. It must never be client_image, which the app
    // silently drops — that made an uploaded sponsor image never show.
    let finalCreativeMode = creativeMode;
    if (uploadVideoUrl) finalCreativeMode = 'client_video';
    else if (uploadImageUrl || artImageUrl) finalCreativeMode = 'client_full_bleed';
    else if (finalCreativeMode === 'client_image') finalCreativeMode = 'admin_authored';

    const allDay = hourStart === 0 && hourEnd === 24;

    const successes: string[] = [];
    const failures: string[] = [];

    const invoiceShares = splitContractAmount(numOrNull(invoiceAmountXcd) ?? 0, typesToPublish.length);

    try {
      for (let typeIndex = 0; typeIndex < typesToPublish.length; typeIndex++) {
        const typeKey = typesToPublish[typeIndex];
        const def = getCampaignTypeByKey(typeKey);
        if (!def) {
          failures.push(`${typeKey} (unknown template)`);
          continue;
        }

        // Skip Map Pin if there are no coords anywhere (override OR
        // brand). Already validated above, but be defensive.
        if (typeKey === 'sponsored_map_pin' &&
            (effectivePinLat === null || effectivePinLng === null)) {
          failures.push(`${def.label} (no pin coordinates)`);
          continue;
        }

        // The placement this campaign should serve on. Passed explicitly
        // so the RPC no longer falls back to the template's single
        // default_placement — critical for the two commute cards that
        // share one template, and for the Passenger Home Banner card.
        const effectivePlacement =
          (festivalMode || mode === 'festival') && def.isFestival
            ? 'festival_event_takeover'
            : def.placement;

        // Build the canonical RPC payload for this template
        const payload: Record<string, unknown> = {
          p_template_key: def.templateKey,
          p_placement: effectivePlacement,
          p_brand_name: selectedBusiness.name,
          p_brand_description: brandBrief || selectedBusiness.description || null,
          p_headline: headline,
          p_body: body || null,
          p_cta_text: ctaText || null,
          p_cta_url: selectedBusiness.website_url || null,
          p_starts_on: startDate,
          p_duration_days: durationDays,
          p_invoice_amount_xcd: invoiceShares[typeIndex] ?? 0,
          p_logo_url: logoUrl || null,
          p_background_color: backgroundColor || null,
          p_corridor_ids: corridorIds.length > 0 ? corridorIds : null,
          p_creative_mode: finalCreativeMode,
          p_creative_image_url: heroUrl || null,
          p_video_url: uploadVideoUrl || null,
          p_client_brief: brandBrief || null,
          p_business_id: selectedBusiness.id,
        };

        // Map-pin-specific fields. The override always wins when present —
        // that's the operator's explicit "drag the pin to here" intent.
        if (typeKey === 'sponsored_map_pin') {
          if (effectivePinLat !== null) payload.p_listing_lat = effectivePinLat;
          if (effectivePinLng !== null) payload.p_listing_lng = effectivePinLng;
          if (selectedBusiness.category) payload.p_listing_category = selectedBusiness.category;
          if (selectedBusiness.phone) payload.p_listing_phone = selectedBusiness.phone;
          if (selectedBusiness.hours_summary) {
            payload.p_listing_hours = selectedBusiness.hours_summary;
          }
          // Pin style override (e.g. 'restaurant', 'bank'). When the
          // operator hasn't picked one, the RPC falls back to the
          // business's existing pin_style on the directory row, which
          // was itself auto-derived from the category at seed time.
          if (store.pinStyle) payload.p_pin_style = store.pinStyle;
        }

        const { data, error } = await supabase.rpc('create_campaign_from_wizard', payload);
        if (error) {
          console.error(`Publish ${def.label} failed:`, error);
          failures.push(`${def.label} (${error.message})`);
          continue;
        }
        const result = (data as any) || {};
        if (result.ok === false) {
          failures.push(`${def.label} (${result.error || 'unknown server error'})`);
          continue;
        }
        const campaignId: string | undefined = result.campaign_id || result.id;
        if (!campaignId) {
          failures.push(`${def.label} (server did not return a campaign id)`);
          continue;
        }

        // Phase 2: advanced fields update for THIS campaign
        const updates: Record<string, unknown> = {};
        if (previewOnly) updates.preview_only = true;
        const dealCode = store.offerCode.trim();
        if (dealCode.length >= 2) {
          updates.offer_code = dealCode;
          updates.offer_terms = store.offerTerms.trim() || null;
        }
        const bid = numOrNull(bidXcd);
        const cpm = numOrNull(cpmRateXcd);
        const flat = numOrNull(flatFeeXcd);
        const dailyCap = numOrNull(maxDailyImpressions);

        if (bid !== null) updates.bid_xcd = bid;
        if (cpm !== null) updates.cpm_rate_xcd = cpm;
        if (flat !== null) updates.flat_fee_xcd = flat;
        if (dailyCap !== null) updates.max_daily_impressions = dailyCap;
        updates.max_impressions_per_day_per_user =
          def.fixedMaxPerUserPerDay ?? maxImpressionsPerDayPerUser;
        updates.cool_down_hours = def.fixedCoolDownHours ?? coolDownHours;
        updates.audience = audience;
        updates.hour_start = allDay ? null : hourStart;
        updates.hour_end   = allDay ? null : Math.min(hourEnd, 23);
        // Anchor the flight window to Dominica time (AST, UTC-4, no DST).
        // The RPC casts p_starts_on::timestamptz in the DB's UTC session, so
        // "starts Oct 24" would go live at 8 PM AST on Oct 23 and auto-kill a
        // day early — not the "11:59 PM on the end date" the Schedule step
        // promises. We override with explicit -04:00 offsets so the window
        // means exactly what the operator sees, regardless of their browser
        // timezone. Start = local midnight; end = 11:59:59 PM on the end date.
        if (startDate) {
          updates.starts_at = new Date(`${startDate}T00:00:00-04:00`).toISOString();
        }
        if (endDate) {
          updates.ends_at = new Date(`${endDate}T23:59:59-04:00`).toISOString();
        }
        // Weather + proximity targeting. Empty/blank = no filter (NULL), so
        // the resolver leaves them island-wide / any-weather exactly as
        // before. A radius only bites when the campaign also has a geo-anchor
        // (set automatically from the linked listing's lat/lng on insert).
        updates.weather_triggers = weatherTriggers.length > 0 ? weatherTriggers : null;
        const radiusKm = numOrNull(targetRadiusKm);
        if (radiusKm !== null) updates.target_radius_km = radiusKm;
        if (invoiceStatus !== 'invoiced') updates.invoice_status = invoiceStatus;

        // Festival fields apply only when the festival flag is on AND the
        // template itself is a festival placement (don't overwrite a
        // commute banner's placement).
        if ((festivalMode || mode === 'festival') && def.isFestival) {
          updates.placement = 'festival_event_takeover';
          if (eventSlug) updates.event_slug = eventSlug;
          if (eventLocationLabel) updates.event_location_label = eventLocationLabel;
        }

        if (Object.keys(updates).length > 0) {
          const { error: updErr } = await supabase
            .from('ad_campaigns')
            .update(updates)
            .eq('id', campaignId);
          if (updErr) {
            console.error(`Advanced settings failed for ${def.label}:`, updErr);
            // The schedule (starts/ends) and targeting live in THIS
            // update. If it fails, the campaign would serve with the
            // wrong dates/targeting, so pause + deactivate it and report
            // the failure rather than pretending the publish succeeded.
            await supabase
              .from('ad_campaigns')
              .update({ is_paused: true, active: false })
              .eq('id', campaignId);
            failures.push(
              `${def.label} (advanced settings failed, so it was paused. Open it in Sponsors, fix the schedule/targeting, then re-activate: ${updErr.message})`,
            );
            continue;
          }
        }

        // Festival linkage — wire this campaign into the
        // festival_sponsorships join table so the admin Festivals
        // dashboard can show the sponsor stack and revenue thermometer.
        // Skipped silently for sponsor-mode campaigns. Only the festival
        // wizard mode populates `selectedFestival` + `festivalTier`.
        if (mode === 'festival' && selectedFestival && festivalTier) {
          const contractedAmount = numOrNull(contractedAmountXcd);
          const { error: linkErr } = await supabase.rpc('admin_link_festival_sponsorship', {
            p_festival_id: selectedFestival.id,
            p_campaign_id: campaignId,
            p_tier: festivalTier,
            p_sub_event_slug: festivalSubEventSlug,
            p_contracted_amount_xcd: contractedAmount,
            p_invoice_status: invoiceStatus || 'pending',
            p_notes: invoiceNumber ? `Invoice #${invoiceNumber}` : null,
          });
          if (linkErr) {
            console.error(`Festival sponsorship link failed for ${def.label}:`, linkErr);
            // Not fatal — the campaign is still live, but the
            // dashboard won't show it as a festival sponsor.
            toast.warning(
              `${def.label} published, but festival link failed: ${linkErr.message}`,
              { duration: 8000 },
            );
          }
        }

        // Optional force-push for this campaign
        if (pushAfterPublish) {
          await supabase.rpc('admin_force_push_campaign', { p_campaign_id: campaignId });
        }

        successes.push(def.label);
      }

      // Final UX
      if (successes.length > 0 && failures.length === 0) {
        // Mention the schedule explicitly in the success toast so the
        // operator never wonders "did it actually publish?" again.
        const hint = scheduleHint;
        const hintLine = previewOnly
          ? 'Team preview only. Open H-Plate on a preview team phone to see it.'
          : hint?.tone === 'live'
            ? 'Live now. Open the H-Plate app to see it.'
            : hint?.tone === 'scheduled'
              ? `Scheduled. Goes live in ${hint.daysUntilStart} day${hint.daysUntilStart === 1 ? '' : 's'}. Use "Preview now" on the Festivals page to QA sooner.`
              : hint?.tone === 'starts_today'
                ? 'Scheduled for tomorrow. Use "Preview now" on the Festivals page to QA sooner.'
                : null;
        toast.success(
          <div className="flex items-start gap-2">
            <PartyPopper className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <div>
                {successes.length === 1
                  ? `Published "${successes[0]}" for ${selectedBusiness.name}`
                  : `Published ${successes.length} campaigns for ${selectedBusiness.name}`}
              </div>
              {hintLine && (
                <div className="text-xs text-gray-300">{hintLine}</div>
              )}
            </div>
          </div>,
          { duration: hintLine ? 9000 : 5000 },
        );
        store.reset();
        onPublished();
        onClose();
      } else if (successes.length > 0 && failures.length > 0) {
        toast.warning(
          `Published ${successes.length} of ${typesToPublish.length}. Failed: ${failures.join('; ')}`,
          { duration: 10000 },
        );
        onPublished();
      } else {
        toast.error(`No campaigns were published. ${failures.join('; ')}`, { duration: 10000 });
      }
    } catch (err: any) {
      toast.error(err.message || 'Publish failed unexpectedly');
    } finally {
      store.set({ publishing: false });
    }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-white">Review &amp; Publish</h2>

      {/* Summary card */}
      <div className="bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 border border-emerald-500/20 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3 pb-3 border-b border-gray-800">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="w-12 h-12 rounded-full object-cover border-2 border-gray-600 shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
              <span className="text-lg font-bold text-emerald-400">
                {selectedBusiness?.name.charAt(0) || '?'}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold truncate">
              {selectedBusiness?.name || '–'}
            </p>
            {typesToPublish.length === 1 && typeDef && (
              <p className="text-[11px] text-emerald-400 flex items-center gap-1">
                <span>{typeDef.icon}</span>
                {typeDef.label}
              </p>
            )}
            {typesToPublish.length > 1 && (
              <p className="text-[11px] text-emerald-400">
                {typesToPublish.length} placements selected. This will create {typesToPublish.length} separate campaigns
              </p>
            )}
          </div>
        </div>

        {/* Multi-template list */}
        {typesToPublish.length > 1 && (
          <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-3 space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Campaigns to create</p>
            {typesToPublish.map((tk, i) => {
              const d = getCampaignTypeByKey(tk);
              if (!d) return null;
              return (
                <div key={tk} className="flex items-center gap-2 text-xs">
                  <span className="text-gray-600 font-mono w-4">{i + 1}.</span>
                  <span className="text-lg leading-none">{d.icon}</span>
                  <span className="text-gray-300 flex-1 truncate">{d.label}</span>
                  <span className="text-[10px] text-gray-500 capitalize">{d.placement.replace(/_/g, ' ')}</span>
                </div>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-2 gap-y-2 text-xs">
          <span className="text-gray-500">Headline</span>
          <span className="text-white truncate">{headline || '–'}</span>

          <span className="text-gray-500">Schedule</span>
          <span className="text-white">
            {fmtLong(startDate)} → {fmtLong(endDate)}
            <span className="text-gray-500 ml-1">({durationDays}d)</span>
          </span>

          {/* Explicit "when does this go live" cue. The mobile
              resolver gates on starts_at ≤ NOW() so a campaign
              scheduled for next month is correctly invisible today
              — operators kept publishing future-dated festival
              campaigns and then thinking they were broken. */}
          {scheduleHint && (
            <>
              <span className="text-gray-500">When</span>
              <span
                className={
                  scheduleHint.tone === 'live'
                    ? 'text-emerald-300'
                    : scheduleHint.tone === 'invalid'
                      ? 'text-rose-300'
                      : 'text-amber-300'
                }
              >
                {scheduleHint.tone === 'live' && '✓ '}
                {scheduleHint.tone === 'scheduled' && '⏳ '}
                {scheduleHint.tone === 'starts_today' && '⏳ '}
                {scheduleHint.tone === 'invalid' && '⚠ '}
                {scheduleHint.message}
              </span>
            </>
          )}

          <span className="text-gray-500">Time window</span>
          <span className="text-white">
            {hourStart === 0 && hourEnd === 24
              ? 'All day'
              : `${String(hourStart).padStart(2, '0')}:00 → ${String(hourEnd).padStart(2, '0')}:00`}
          </span>

          <span className="text-gray-500">Audience</span>
          <span className="text-white capitalize">{audience}</span>

          <span className="text-gray-500">Corridors</span>
          <span className="text-white text-xs">
            {corridorIds.length === 0 ? 'All corridors' : `${corridorIds.length} selected`}
          </span>

          <span className="text-gray-500">Creative</span>
          <span className="text-white">{creativeMode.replace(/_/g, ' ')}</span>

          <span className="text-gray-500">Invoice</span>
          <span className="text-white">
            {invoiceAmountXcd ? `$${invoiceAmountXcd} XCD` : '–'}
            {invoiceAmountXcd && typesToPublish.length > 1 && (
              <span className="text-gray-500 ml-1">
                split over {typesToPublish.length} campaigns
              </span>
            )}
            <span className="text-gray-500 ml-1 capitalize">({invoiceStatus})</span>
          </span>

          {festivalMode && (
            <>
              <span className="text-gray-500">Event</span>
              <span className="text-amber-400 truncate">
                🎶 {eventSlug || '–'} {eventLocationLabel ? `· ${eventLocationLabel}` : ''}
              </span>
            </>
          )}

          {typesToPublish.includes('sponsored_map_pin') && (() => {
            const ps = resolvePinStyle(store.pinStyle, selectedBusiness?.category ?? null);
            const Icon = ps.icon;
            return (
              <>
                <span className="text-gray-500">Map pin</span>
                <span className="text-white inline-flex items-center gap-1.5">
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-white/40"
                    style={{ backgroundColor: ps.bg }}
                  >
                    <Icon className="w-2.5 h-2.5" style={{ color: ps.fg }} />
                  </span>
                  {ps.label}
                </span>
              </>
            );
          })()}
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-1">
          {errors.map((e, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-red-400">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {e}
            </div>
          ))}
        </div>
      )}

      {/* Team preview checkbox */}
      <label
        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
          hasPinType
            ? 'border-gray-800 opacity-60 cursor-not-allowed'
            : teamPreviewOnly
              ? 'border-violet-500/60 bg-violet-500/5 cursor-pointer'
              : 'border-gray-700 hover:border-gray-600 cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          checked={previewOnly}
          disabled={hasPinType}
          onChange={(e) => store.set({ teamPreviewOnly: e.target.checked })}
          className="w-4 h-4 rounded border-gray-600 text-violet-500 focus:ring-violet-500 bg-gray-800"
        />
        <div>
          <span className="text-sm text-white font-medium flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-violet-400" />
            Team preview only (sample for a pitch)
          </span>
          <p className="text-xs text-gray-500">
            {hasPinType
              ? 'Not available for map pins: a pin marks the business as sponsored for everyone.'
              : 'Only the preview team sees it in the app, so you can show a business their ad on a real phone before they pay. Turn it off on the Sponsors page when they sign.'}
          </p>
        </div>
      </label>

      {/* Push checkbox */}
      <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-700 hover:border-gray-600 cursor-pointer transition-colors">
        <input
          type="checkbox"
          checked={pushAfterPublish}
          onChange={(e) => store.set({ pushAfterPublish: e.target.checked })}
          className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 bg-gray-800"
        />
        <div>
          <span className="text-sm text-white font-medium flex items-center gap-1.5">
            <Rocket className="w-3.5 h-3.5 text-amber-400" />
            Force-push to all devices now
          </span>
          <p className="text-xs text-gray-500">
            Wipes any previous dismissals so this ad surfaces immediately,
            even on phones that recently saw a different ad in the same
            placement.
          </p>
        </div>
      </label>

      {/* Publish button */}
      <Button
        onClick={handlePublish}
        disabled={!canPublish}
        className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white h-12 text-sm font-semibold shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {publishing ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Publishing {typesToPublish.length > 1 ? `${typesToPublish.length} campaigns` : 'campaign'}…
          </>
        ) : (
          <>
            <Check className="w-4 h-4 mr-2" />
            {typesToPublish.length > 1
              ? `Publish ${typesToPublish.length} campaigns`
              : 'Publish campaign'}
          </>
        )}
      </Button>

      {/* Optional debug — RPC payload */}
      <button
        type="button"
        onClick={() => setShowJson((s) => !s)}
        className="text-[10px] text-gray-600 hover:text-gray-400 flex items-center gap-1"
      >
        <ChevronDown
          className={`w-3 h-3 transition-transform ${showJson ? 'rotate-180' : ''}`}
        />
        {showJson ? 'Hide' : 'Show'} server payload (debug)
      </button>
      {showJson && (
        <pre className="text-[10px] bg-gray-950 border border-gray-800 rounded p-2 overflow-x-auto text-gray-400 font-mono whitespace-pre-wrap">
          {JSON.stringify(
            {
              p_template_key: templateKey,
              p_brand_name: selectedBusiness?.name,
              p_starts_on: startDate,
              p_duration_days: durationDays,
              p_invoice_amount_xcd: invoiceAmountXcd,
              p_corridor_ids: corridorIds,
              p_business_id: selectedBusiness?.id,
              advanced_update: {
                bid_xcd: bidXcd,
                cool_down_hours: coolDownHours,
                hour_start: hourStart,
                hour_end: hourEnd,
                audience,
              },
            },
            null,
            2,
          )}
        </pre>
      )}
    </div>
  );
}
